import TickIcon from "@src/app/assets/icons/tick.svg?react";
import viewportState from "@src/app/stores/viewport";
import cx from "classnames";
import { useEffect, useState } from "react";

// Toast Controller
type ToastPayload = {
  title: string;
  visible: boolean;
};

const listeners = new Set<(p: ToastPayload) => void>();

const toastController = {
  show(payload: Omit<ToastPayload, "visible">) {
    listeners.forEach((l) => {
      l({ ...payload, visible: true });
    });
  },

  hide() {
    listeners.forEach((l) => {
      l({ title: "", visible: false });
    });
  },

  subscribe(callback: (p: ToastPayload) => void) {
    listeners.add(callback);
    return () => listeners.delete(callback);
  },
};

// Toast Component
export function ShareToastContainer() {
  const { isMobileView } = viewportState;
  const [toast, setToast] = useState<ToastPayload>({
    title: "",
    visible: false,
  });

  useEffect(() => {
    const unsubscribe = toastController.subscribe((data) => {
      setToast(data);
    });
    return () => {
      unsubscribe();
    };
  }, []);

  if (!toast.visible) return null;

  return (
    <div
      style={
        isMobileView
          ? {
              bottom: `calc(env(safe-area-inset-bottom) + 20px)`,
              left: "20px",
              right: "20px",
              marginLeft: "auto",
              marginRight: "auto",
            }
          : {
              bottom: `calc(env(safe-area-inset-bottom) + 20px)`,
              left: "20px",
              width: "319px",
            }
      }
      className={cx(
        "z-[100] fixed",
        "bg-white text-button-secondary-label text-xs",
        "p-2 shadow-md rounded-[4px] flex gap-2 items-center",
        "animate-fade-in-up",
      )}
    >
      <TickIcon width={26} height={26} className="ml-[6px]" />
      <span className="py-[6px]">{toast.title}</span>
    </div>
  );
}

// Native Share Function
export function nativeShare({
  title,
  text,
  url,
  toastTitle,
  fallbackToastTitle,
  autoClose = 1200,
}: {
  title: string;
  text: string;
  url: string;
  toastTitle: string;
  fallbackToastTitle: string;
  autoClose?: number;
}) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (window.navigator && window.navigator.share) {
    void window.navigator.share({ title, text, url }).then(() => {
      toastController.show({
        title: toastTitle,
      });

      setTimeout(() => {
        toastController.hide();
      }, autoClose);
    });
  } else {
    // fallback to copy to clipboard
    void window.navigator.clipboard.writeText(url).then(() => {
      toastController.show({
        title: fallbackToastTitle,
      });

      setTimeout(() => {
        toastController.hide();
      }, autoClose);
    });
  }
}
